import { api } from '../slices/api';

export type NeedStatus = 'Open' | 'Fulfilled' | 'Dismissed' | 'Auto Cleared';

export type CandidateBucketKey =
	| 'original_bar'
	| 'original_material'
	| 'substitute_certified'
	| 'substitute_uncertified';

/**
 * Whether the machine on a job is where it is being made right now or only
 * where the scheduler intends to make it. ERP keeps both in one column, so the
 * distinction has to travel with the name -- see `resolve_mill_assignment`.
 */
export type MachineAssignmentStatus = 'on_machine_now' | 'scheduled';

export interface NeedOrderLineItem {
	id: string;
	name: string | null;
	order_name: string | null;
	quantity: number | null;
	status: string | null;
	machine_name: string | null;
	machine_status: MachineAssignmentStatus | null;
	/** Part image, or null when the job has none on file. */
	thumbnail_url: string | null;
	cam_programming_status: string | null;
	partsbadger_production_method: string | null;
	required_partner_ship_date: string | null;
}

export interface ShopFloorMaterialNeed {
	id: string;
	order_line_item: NeedOrderLineItem;
	requested_quantity: number;
	remaining_quantity: number;
	reason: string | null;
	reason_label: string | null;
	notes: string | null;
	machine_name: string | null;
	requested_by_name: string | null;
	/** Building of the requesting machine. Null when the site did not resolve. */
	building: string | null;
	building_name: string | null;
	/** Site name as SFA sent it, kept for diagnosing an unresolved building. */
	source_building_name: string | null;
	status: NeedStatus;
	status_changed_at: string | null;
	fulfilled_by: string | null;
	created_time: string;
}

export interface CandidateBarLocation {
	building: string | null;
	rack_code: string | null;
	rack: string | null;
	shelf: string | null;
	code: string | null;
	/** Operator shorthand, e.g. "X1-A6". */
	short_code: string | null;
	/** App-wide label, e.g. "X1 - Shelf 1 - A6". */
	display_name: string | null;
}

/** Raw cross-section numbers the shape preview needs to draw in proportion. */
export interface MaterialCrossSection {
	height: string | null;
	width: string | null;
	outer_diameter: string | null;
	inner_diameter: string | null;
	wall_thickness: string | null;
}

export interface CandidateBar extends MaterialCrossSection {
	material_length_id: string;
	friendly_id: string | null;
	bundle_friendly_id: string | null;
	material_id: string;
	material_display: string;
	material_display_size: string | null;
	shape: string | null;
	type: string | null;
	category: string | null;
	current_length: string;
	available_length: string;
	required_length: string;
	max_pieces: number;
	/** True when this one bar can yield the whole requested quantity. */
	covers_full_request: boolean;
	/** Pieces this bar leaves uncovered; 0 when it covers the request. */
	shortfall_pieces: number;
	has_certificate: boolean;
	is_remnant: boolean;
	location: CandidateBarLocation;
}

export interface CandidateBucket {
	key: CandidateBucketKey;
	label: string;
	requires_certificate_ack: boolean;
	bars: CandidateBar[];
}

export interface SourceReservationSpec extends MaterialCrossSection {
	id: string;
	material_cut_length: string;
	saw_kerf: string;
	required_trim_cut_length: string | null;
	material_display: string;
	display_size: string | null;
	shape: string | null;
	type: string | null;
	category: string | null;
	material_friendly_id: string | null;
	is_swiss: boolean;
}

export interface CandidatesResponse {
	source_reservation: SourceReservationSpec;
	/** Inches one bar needs free to cover the whole request. */
	required_length: string;
	/** Inches to yield a single piece -- the floor for being listed at all. */
	minimum_length: string;
	quantity: number;
	/** False when the request can only be filled by combining bars. */
	full_coverage_available: boolean;
	/**
	 * The single bar to steer the operator to, so the whole request comes off
	 * one bar in one saw setup. Null when no bar can cover it alone.
	 */
	recommended_material_length_id: string | null;
	empty_reason: { code: string; message: string } | null;
	buckets: CandidateBucket[];
}

export interface CutLengthOption {
	reservation_id: string;
	material_cut_length: string;
	saw_kerf: string;
	material_id: string;
	material_display: string;
	created_time: string;
}

export interface QueuePlacement {
	merged_into_existing_group: boolean;
	on_cutting_queue: boolean;
	cam_programming_status: string | null;
	message: string;
}

export interface CreateReservationResponse {
	reservation_id: string;
	bucket: CandidateBucketKey;
	bucket_label: string;
	quantity: number;
	material_cut_length: string;
	reserved_length: string;
	material_friendly_id: string | null;
	need: ShopFloorMaterialNeed | null;
	queue: QueuePlacement;
}

export interface ReservationAudit {
	id: string;
	reservation: string;
	source_reservation: string | null;
	need: ShopFloorMaterialNeed | null;
	bucket: CandidateBucketKey;
	is_substitution: boolean;
	operator_name: string | null;
	certificate_acknowledged: boolean;
	certificate_acknowledged_by: string | null;
	certificate_acknowledged_at: string | null;
	order_name: string | null;
	part_name: string | null;
	material_display: string | null;
	material_friendly_id: string | null;
	material_cut_length: string | null;
	quantity: number | null;
	created_time: string;
}

export const shopFloorNeedsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getShopFloorMaterialNeeds: builder.query<
			ShopFloorMaterialNeed[],
			{
				status?: string;
				search?: string;
				order_line_item?: string;
				building_id?: string;
			} | void
		>({
			query: (params) => ({
				url: 'material/shop-floor-needs/',
				params: (params || undefined) as Record<string, string> | undefined,
			}),
			providesTags: ['ShopFloorMaterialNeed'],
		}),

		getOpenMaterialNeedCount: builder.query<
			{ open: number },
			{ building_id?: string } | void
		>({
			query: (params) => ({
				url: 'material/shop-floor-needs/open-count/',
				params: (params || undefined) as Record<string, string> | undefined,
			}),
			providesTags: ['ShopFloorMaterialNeed'],
		}),

		getMaterialNeedCutOptions: builder.query<
			{ options: CutLengthOption[] },
			{ order_line_item: string }
		>({
			query: (params) => ({
				url: 'material/shop-floor-needs/cut-options/',
				params,
			}),
		}),

		searchReservableJobs: builder.query<
			{ results: NeedOrderLineItem[] },
			{ search: string }
		>({
			query: (params) => ({
				url: 'material/shop-floor-needs/job-search/',
				params,
			}),
		}),

		// Deliberately not polled: the list is only valid for the moment it was
		// asked for, and it is re-fetched on demand after a 409.
		getMaterialNeedCandidates: builder.query<
			CandidatesResponse,
			{
				order_line_item: string;
				quantity: number;
				source_reservation?: string;
			}
		>({
			query: (params) => ({
				url: 'material/shop-floor-needs/candidates/',
				params: params as unknown as Record<string, string>,
			}),
		}),

		createShopFloorReservation: builder.mutation<
			CreateReservationResponse,
			{
				values: {
					order_line_item: string;
					material_length: string;
					quantity: number;
					source_reservation?: string;
					need?: string;
					operator_name?: string;
					certificate_acknowledged?: boolean;
				};
				idempotencyKey?: string;
			}
		>({
			query: ({ values, idempotencyKey }) => ({
				url: 'material/shop-floor-needs/create-reservation/',
				method: 'POST',
				body: values,
				...(idempotencyKey
					? { headers: { 'Idempotency-Key': idempotencyKey } }
					: {}),
			}),
			invalidatesTags: [
				'ShopFloorMaterialNeed',
				'ShopFloorReservationAudit',
				'OrderLineItem',
			],
		}),

		dismissShopFloorMaterialNeed: builder.mutation<
			ShopFloorMaterialNeed,
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `material/shop-floor-needs/${id}/dismiss/`,
				method: 'POST',
			}),
			invalidatesTags: ['ShopFloorMaterialNeed'],
		}),

		getShopFloorReservationAudits: builder.query<
			{ count: number; results: ReservationAudit[] } | ReservationAudit[],
			{ bucket?: string; certificate_acknowledged?: string; search?: string } | void
		>({
			query: (params) => ({
				url: 'material/shop-floor-reservation-audits/',
				params: (params || undefined) as Record<string, string> | undefined,
			}),
			providesTags: ['ShopFloorReservationAudit'],
		}),

		getShopFloorReservationAuditSummary: builder.query<
			{
				total: number;
				substitutions: number;
				uncertified: number;
				open_needs: number;
			},
			void
		>({
			query: () => 'material/shop-floor-reservation-audits/summary/',
			providesTags: ['ShopFloorReservationAudit', 'ShopFloorMaterialNeed'],
		}),
	}),
});

export const {
	useGetShopFloorMaterialNeedsQuery,
	useGetOpenMaterialNeedCountQuery,
	useGetMaterialNeedCutOptionsQuery,
	useSearchReservableJobsQuery,
	useLazySearchReservableJobsQuery,
	useGetMaterialNeedCandidatesQuery,
	useLazyGetMaterialNeedCandidatesQuery,
	useCreateShopFloorReservationMutation,
	useDismissShopFloorMaterialNeedMutation,
	useGetShopFloorReservationAuditsQuery,
	useGetShopFloorReservationAuditSummaryQuery,
} = shopFloorNeedsApi;
